/**
 * FilterBar — filter and sort controls for the suggestion list.
 */
import { useState, useEffect, useCallback } from 'react';
import type { FilterState, SortField, ProviderSpecialty } from '../types';
import { PROVIDER_SPECIALTIES } from '../utils/specialtyMapping';

interface Props {
  filters: FilterState;
  onChange: (updated: Partial<FilterState>) => void;
  totalCount: number;
  filteredCount: number;
  providerSpecialty: ProviderSpecialty | null;
}

export function FilterBar({ filters, onChange, totalCount, filteredCount, providerSpecialty }: Props) {
  const [localSearch, setLocalSearch] = useState(filters.searchQuery);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      onChange({ searchQuery: localSearch });
    }, 250);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  useEffect(() => {
    setLocalSearch(filters.searchQuery);
  }, [filters.searchQuery]);

  const handleClear = useCallback(() => {
    setLocalSearch('');
    onChange({ searchQuery: '' });
  }, [onChange]);

  const activeFilterCount = [
    filters.gapType !== 'all',
    filters.status !== 'all',
    filters.specialty !== 'all',
    filters.hccOnly,
    filters.mySpecialtyOnly,
  ].filter(Boolean).length;

  return (
    <div className="px-4 py-2 border-b border-gray-200 bg-white">
      {/* Search row + filter toggle */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder="Search ICD code or description…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none placeholder-gray-400 transition"
            aria-label="Search coding suggestions"
          />
          {localSearch && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-1 px-2 py-1 rounded text-2xs font-medium border transition-colors ${
            activeFilterCount > 0 || expanded
              ? 'border-blue-300 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:border-gray-300'
          }`}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        <span className="text-2xs text-gray-500 flex-shrink-0 whitespace-nowrap">
          {filteredCount === totalCount
            ? `${totalCount} suggestions`
            : `${filteredCount} of ${totalCount}`}
        </span>
      </div>

      {/* Collapsible filters row */}
      {expanded && (
      <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t border-gray-100">
        {/* Gap type */}
        <FilterSelect
          value={filters.gapType}
          onChange={(v) => onChange({ gapType: v as FilterState['gapType'] })}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'suspect_new', label: 'Suspecting Code Gap' },
            { value: 'opportunity', label: 'Recaptured Opportunity' },
            { value: 'recapture', label: 'Recaptured Existing' },
            { value: 'recapture_stale', label: 'Suspecting Emerging' },
            { value: 'over_claimed', label: 'Recaptured Risk' },
          ]}
        />

        {/* Status */}
        <FilterSelect
          value={filters.status}
          onChange={(v) => onChange({ status: v as FilterState['status'] })}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'pending', label: 'Pending' },
            { value: 'accepted', label: 'Accepted' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />

        {/* Specialty */}
        <FilterSelect
          value={filters.specialty}
          onChange={(v) => onChange({ specialty: v as FilterState['specialty'] })}
          options={[
            { value: 'all', label: 'All Specialties' },
            ...PROVIDER_SPECIALTIES.map((s) => ({ value: s, label: s })),
          ]}
        />

        {/* My Specialty toggle — only when provider specialty is known */}
        {providerSpecialty && (
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.mySpecialtyOnly}
              onChange={(e) => onChange({ mySpecialtyOnly: e.target.checked })}
              className="rounded text-blue-600 focus:ring-blue-400 w-3.5 h-3.5"
            />
            <span className="text-2xs text-gray-600 font-medium">My Specialty</span>
          </label>
        )}

        {/* HCC only toggle */}
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.hccOnly}
            onChange={(e) => onChange({ hccOnly: e.target.checked })}
            className="rounded text-blue-600 focus:ring-blue-400 w-3.5 h-3.5"
          />
          <span className="text-2xs text-gray-600 font-medium">HCC Only</span>
        </label>

        {/* Sort — pushed to far right */}
        <div className="flex items-center gap-1 ml-auto">
          <FilterSelect
            value={filters.sortBy}
            onChange={(v) => onChange({ sortBy: v as SortField })}
            options={[
              { value: 'clinicalPriority', label: 'Clinical Priority' },
              { value: 'rafImpact', label: 'RAF Impact' },
              { value: 'gapType', label: 'Gap Type' },
              { value: 'icdCode', label: 'ICD Code' },
            ]}
          />
          <button
            onClick={() => onChange({ sortDir: filters.sortDir === 'asc' ? 'desc' : 'asc' })}
            className="p-1.5 border border-gray-200 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition"
            aria-label={`Sort ${filters.sortDir === 'asc' ? 'descending' : 'ascending'}`}
            title={filters.sortDir === 'asc' ? 'Sort descending' : 'Sort ascending'}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {filters.sortDir === 'asc' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
              )}
            </svg>
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-2xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 cursor-pointer hover:border-gray-300 transition"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
